import api from './api'
import {
  HippaConsentData,
  HippaConsentResponse,
  PatientConsentData,
  PatientConsentResponse,
  FamilyConsentData,
  FamilyConsentResponse,
} from '@/types'
import {
  AgencySubscriptionAgreementSignaturePayload,
  AgencySubscriptionAgreementSignatureResponse,
} from '@/types/subscriptionAgreement'

/**
 * Service for handling consent operations (HIPAA for providers, Intake for patients)
 */
export class ConsentService {
  /**
   * Submit HIPAA consent signature (for therapists/providers)
   */
  static async submitHippaConsent(
    consentData: HippaConsentData
  ): Promise<HippaConsentResponse> {
    const response = await api.post<HippaConsentResponse>(
      '/agreements/signed/therapist',
      consentData
    )
    return response.data
  }

  /**
   * Get HIPAA consent status (optional, for checking status)
   */
  static async getHippaConsentStatus(): Promise<{ hasHippaSigned: boolean }> {
    const response = await api.get<{ hasHippaSigned: boolean }>(
      '/therapist/hippa-consent/status'
    )
    return response.data
  }

  /**
   * Submit Patient Intake & Consent Packet signature
   */
  static async submitPatientConsent(
    consentData: PatientConsentData
  ): Promise<PatientConsentResponse> {
    const response = await api.post<PatientConsentResponse>(
      '/agreements/signed/patient',
      consentData
    )
    return response.data
  }

  /**
   * Submit HIPAA consent signature (for agency users)
   */
  static async submitAgencyHippaConsent(
    consentData: HippaConsentData
  ): Promise<HippaConsentResponse> {
    const response = await api.post<HippaConsentResponse>(
      '/agreements/signed/agency',
      consentData
    )
    return response.data
  }

  /**
   * Submit Family Member / Caregiver Access Consent signature
   */
  static async submitFamilyConsent(
    consentData: FamilyConsentData
  ): Promise<FamilyConsentResponse> {
    const response = await api.post<FamilyConsentResponse>(
      '/agreements/signed/family',
      consentData
    )
    return response.data
  }

  /**
   * Submit Agency Platform Subscription Agreement signature
   */
  static async submitAgencySubscriptionAgreement(
    consentData: AgencySubscriptionAgreementSignaturePayload
  ): Promise<AgencySubscriptionAgreementSignatureResponse> {
    const response = await api.post<AgencySubscriptionAgreementSignatureResponse>(
      '/agreements/signed/agency-subscription-agreement',
      consentData
    )
    return response.data
  }
}
